"use client";

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Check, X, Coffee, Sparkles, Zap, ShieldCheck, ArrowRight, Heart, Gift } from 'lucide-react';
import Image from 'next/image';

const features = [
  { name: "Subdominio propio (.snapsite.cc)", free: true, premium: true },
  { name: "Código QR descargable", free: true, premium: true },
  { name: "Tarjeta de presentación PNG (frente y reverso)", free: true, premium: true },
  { name: "Tarjeta de contacto .VCF", free: true, premium: true },
  { name: "Compartir nativo", free: true, premium: true },
  { name: "Links inteligentes (WhatsApp, Tel, Mail)", free: true, premium: true },
  { name: "Botón compartir WhatsApp", free: true, premium: true },
  { name: "Icono de Google Maps", free: true, premium: true },
  { name: "Combinaciones de fuentes", free: "Estándar", premium: "8 Pares Pro" },
  { name: "Layouts de diseño", free: "Solo Classic", premium: "9 Layouts Pro" },
  { name: "Colores de fondo personalizados", free: false, premium: true },
  { name: "Sin marca de SnapSite (No Branding)", free: false, premium: true },
  { name: "Animación de iconos opcional", free: false, premium: true },
  { name: "Incrustar Iframe de Perfil", free: true, premium: true },
  { name: "Widget de Burbuja Flotante", free: false, premium: true },
  { name: "Galería visual de hasta 8 imágenes", free: false, premium: true },
  { name: "Publicación instantánea (sin revisión)", free: false, premium: true },
  { name: "Acceso anticipado a funciones", free: false, premium: true },
];

export default function PreciosPage() {
  return (
    <main className="min-h-screen bg-background text-foreground pb-20 overflow-x-hidden">
      {/* Fondo con orbes (estilo Landing) */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-violet-500/10 blur-3xl" />
      </div>

      {/* Navegación del Header */}
      <header className="relative z-20 w-full px-6 py-4 flex items-center justify-between max-w-5xl mx-auto">
        <Link href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <img src="/logo_snapsite.svg" alt="Logo SnapSite" width={32} height={32} className="rounded-md" />
          <span className="font-bold text-lg tracking-tight">SnapSite</span>
        </Link>
        <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground">
          <Link href="/">Volver al inicio</Link>
        </Button>
      </header>

      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-8 md:pt-12">
        <div className="text-center space-y-4 mb-16">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight">
            Precios <span className="text-primary">Transparentes</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Sin trucos, sin suscripciones mensuales. Solo una herramienta potente para tu presencia digital.
          </p>
        </div>

        {/* Cards de Precios */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {/* Plan Gratis */}
          <div className="rounded-3xl border bg-card/50 backdrop-blur-md p-8 flex flex-col">
            <div className="mb-8">
              <h3 className="text-2xl font-bold">Plan Inicial</h3>
              <p className="text-muted-foreground">Para empezar tu camino digital.</p>
              <div className="mt-4 flex items-baseline">
                <span className="text-4xl font-extrabold">$0</span>
                <span className="ml-1 text-muted-foreground">Gratis para siempre</span>
              </div>
            </div>
            <ul className="space-y-4 mb-8 flex-grow">
              <li className="flex items-center gap-3 text-sm">
                <Check className="text-green-500 h-5 w-5" /> Compartir nativo
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Check className="text-green-500 h-5 w-5" /> Links inteligentes (WhatsApp, Mail...)
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Check className="text-green-500 h-5 w-5" /> Botón compartir directo por WhatsApp
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Check className="text-green-500 h-5 w-5" /> Icono de Google Maps
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <X className="h-5 w-5" /> Layouts adicionales y colores pro
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <X className="h-5 w-5" /> Sin marca de SnapSite
              </li>
            </ul>
            <Button variant="outline" size="lg" className="w-full h-12" asChild>
              <Link href="/dashboard">Empezar gratis</Link>
            </Button>
          </div>

          {/* Plan Premium */}
          <div className="rounded-3xl border-2 border-primary bg-card p-8 flex flex-col relative shadow-2xl shadow-primary/10 scale-105 md:scale-110">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
              Recomendado
            </div>
            <div className="mb-8">
              <h3 className="text-2xl font-bold flex items-center gap-2">
                Premium <Sparkles className="h-5 w-5 text-primary fill-primary" />
              </h3>
              <p className="text-muted-foreground">Todo el poder de SnapSite.</p>
              <div className="mt-4 flex items-baseline">
                <span className="text-4xl font-extrabold">$5 USD</span>
                <span className="ml-2 text-primary font-semibold text-sm">Pago único</span>
              </div>
            </div>
            <ul className="space-y-4 mb-8 flex-grow">
              <li className="flex items-center gap-3 text-sm">
                <Zap className="text-primary h-5 w-5 fill-primary/20" /> <strong>9 Temas Pro + 8 Fuentes + 16 Colores = Perfil Único</strong>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Zap className="text-primary h-5 w-5 fill-primary/20" /> <strong>Animación de iconos opcional</strong>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Zap className="text-primary h-5 w-5 fill-primary/20" /> <strong>Widget de Burbuja Flotante</strong>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Zap className="text-primary h-5 w-5 fill-primary/20" /> <strong>Acceso anticipado a funciones</strong>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Check className="text-green-500 h-5 w-5" /> Publicación instantánea y sin marca
              </li>
              <li className="flex items-center gap-3 text-sm text-primary/80">
                <Heart className="h-5 w-5 fill-primary/20" /> Apoyas a los DevMonkeys
              </li>
            </ul>
            <Button size="lg" className="w-full h-12 text-base shadow-lg shadow-primary/30" asChild>
              <Link href="/dashboard">Activar Premium</Link>
            </Button>
            <p className="text-[10px] text-center text-muted-foreground mt-4 italic">
              * Se activa al instante tras el pago. Sin tarjetas guardadas.
            </p>
          </div>
        </div>

        {/* Banner cross-sell .Site de RTSI */}
        <div className="mb-20 p-6 md:p-8 rounded-3xl bg-primary/5 border border-primary/20 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="space-y-1">
            <h2 className="text-xl md:text-2xl font-bold">¿Quieres un sitio web completo?</h2>
            <p className="text-muted-foreground text-sm md:text-base">
              En <strong>.Site de RTSI</strong> lo hacemos por ti. Diseño profesional, hosting y dominio para llevar tu negocio al siguiente nivel.
            </p>
          </div>
          <Button variant="outline" asChild className="border-primary/50 hover:bg-primary/10 transition-colors shrink-0">
            <a href="https://rtsi.site" target="_blank" rel="noopener noreferrer">Conoce más <ArrowRight className="ml-2 h-4 w-4" /></a>
          </Button>
        </div>

        {/* Nota "Por qué un café" */}
        <section className="max-w-3xl mx-auto mb-20 p-8 rounded-3xl bg-muted/50 border text-center space-y-4">
          <div className="bg-background w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-2 shadow-sm">
            <Coffee className="text-primary h-6 w-6" />
          </div>
          <h2 className="text-2xl font-bold">¿Por qué un pago único?</h2>
          <p className="text-muted-foreground leading-relaxed">
            Odiamos las suscripciones tanto como tú. Creemos que una tarjeta digital no debería ser un gasto mensual. 
            Al invitarnos a un "café" ($5 USD), nos ayudas con los costos de servidores y a que los <strong>DevMonkeys</strong> sigan mejorando SnapSite para todos. 
            Tú obtienes funciones pro para siempre, y nosotros mantenemos el proyecto vivo.
          </p>
        </section>

        {/* Sistema de Referidos — la alternativa a pagar */}
        <section className="max-w-3xl mx-auto mb-20 p-8 rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 text-center space-y-5">
          <div className="bg-background w-12 h-12 rounded-2xl flex items-center justify-center mx-auto shadow-sm">
            <Image src="/badges/premium_sombra.svg" alt="Insignia Premium" width={32} height={32} />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold">¿Prefieres no pagar? Consíguelo gratis</h2>
            <p className="text-muted-foreground leading-relaxed max-w-xl mx-auto">
              Crea tu SnapSite gratis, comparte tu enlace único con tus amigos, y cuando <strong>5 personas se registren</strong> con
              tu link, Premium se activa solo — sin pagar nada. Sigue invitando y desbloquea insignias que se lucen en tu perfil.
            </p>
          </div>

          <div className="flex justify-center gap-2 sm:gap-4">
            {[
              { slug: 'premium', label: 'Premium' },
              { slug: 'bronce', label: 'Bronce' },
              { slug: 'plata', label: 'Plata' },
              { slug: 'oro', label: 'Oro' },
              { slug: 'platino', label: 'Platino' },
            ].map((badge) => (
              <div key={badge.slug} className="flex flex-col items-center gap-1">
                <Image src={`/badges/${badge.slug}_sombra.svg`} alt={`Insignia ${badge.label}`} width={44} height={44} className="w-8 h-8 sm:w-11 sm:h-11" />
                <span className="text-[0.65rem] sm:text-xs text-muted-foreground font-medium">{badge.label}</span>
              </div>
            ))}
          </div>

          <Button size="lg" className="font-semibold shadow-md gap-2" asChild>
            <Link href="/crear">
              <Gift className="h-4 w-4" /> Crear mi SnapSite y empezar a invitar
            </Link>
          </Button>
        </section>

        {/* Tabla Comparativa */}
        <div className="hidden md:block mb-20 overflow-hidden rounded-2xl border">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="p-4 font-semibold">Característica</th>
                <th className="p-4 font-semibold text-center">Gratis</th>
                <th className="p-4 font-semibold text-center text-primary">Premium</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {features.map((f) => (
                <tr key={f.name} className="hover:bg-muted/30 transition-colors">
                  <td className="p-4">{f.name}</td>
                  <td className="p-4 text-center">
                    {typeof f.free === 'boolean' ? (f.free ? <Check className="mx-auto h-4 w-4 text-green-500" /> : <X className="mx-auto h-4 w-4 text-muted-foreground/30" />) : f.free}
                  </td>
                  <td className="p-4 text-center font-medium">
                    {typeof f.premium === 'boolean' ? (f.premium ? <Check className="mx-auto h-4 w-4 text-primary" /> : <X className="mx-auto h-4 w-4" />) : f.premium}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Consejos Rápidos */}
        <div className="grid md:grid-cols-3 gap-6 mb-20">
          <div className="space-y-2 p-6 rounded-2xl border bg-card/30">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs mb-2">1</div>
            <h4 className="font-bold">Foto de Impacto</h4>
            <p className="text-sm text-muted-foreground">Usa una imagen clara y profesional que muestre tu marca personal.</p>
          </div>
          <div className="space-y-2 p-6 rounded-2xl border bg-card/30">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs mb-2">2</div>
            <h4 className="font-bold">Bio Concisa</h4>
            <p className="text-sm text-muted-foreground">Tienes 500 caracteres. Describe quién eres y qué resuelves con claridad.</p>
          </div>
          <div className="space-y-2 p-6 rounded-2xl border bg-card/30">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs mb-2">3</div>
            <h4 className="font-bold">Mantente al día</h4>
            <p className="text-sm text-muted-foreground">Refresca tus links o biografía cuando tengas novedades o nuevos proyectos.</p>
          </div>
        </div>

        {/* Mascota Final */}
        <div className="flex flex-col items-center gap-4 py-10">
           <Image
              src="/devmonkeyshome.webp"
              alt="DevMonkeys celebrando"
              width={360}
              height={240}
              className="rounded-lg opacity-80"
              unoptimized
            />
            <p className="text-sm text-muted-foreground italic text-center max-w-xs">
              "Nuestro equipo de monos programadores te agradece el apoyo."
            </p>
            <Button variant="link" asChild>
              <Link href="/dashboard" className="flex items-center gap-2">
                Volver al Panel <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
        </div>

      </div>

      <footer className="mt-auto py-10 text-center border-t text-xs text-muted-foreground">
        <div className="flex justify-center gap-4 mb-2">
          <Link href="/reportar" className="hover:text-primary hover:underline">Reportar contenido</Link>
          <Link href="/changue-log" className="hover:text-primary hover:underline">Novedades</Link>
          <Link href="/privacidad" className="hover:text-primary hover:underline">Privacidad</Link>
          <Link href="/terminos" className="hover:text-primary hover:underline">Términos</Link>
        </div>
        SnapSite de .Site RTSI — 2026
      </footer>
    </main>
  );
}
